import styled from '@emotion/styled';
// eslint-disable-next-line no-restricted-imports
import { SimpleGrid, Title } from '@mantine/core';

type StickyHeader = {
  isSidePanel?: boolean;
  isChatPanelOpen?: boolean;
  chatOffset?: number;
};

const customProps = ['isSidePanel', 'isChatPanelOpen', 'chatOffset'];

const PageHeader = styled(SimpleGrid, {
  shouldForwardProp: (prop) => !customProps.includes(prop),
})<StickyHeader>(({ theme, isSidePanel, isChatPanelOpen, chatOffset: chatOffsetProp = 0 }) => {
  const chatOffset = chatOffsetProp;

  return `
    background-color: ${theme.white};
    z-index: 4;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    position: relative;
    transition: padding 0.3s ease-in-out, gap 0.3s ease-in-out;

    ${
      isSidePanel
        ? `
      padding-left: ${theme.spacing.sm}px;
      padding-right: ${theme.spacing.sm}px;
      padding-bottom: ${theme.spacing.md}px;
      padding-top: ${theme.spacing.xl}px;
      gap: 0,
      top: 0;
    `
        : `
      padding-top: ${theme.spacing.xl}px;
      padding-bottom: ${theme.spacing.xl}px;
      gap: ${theme.spacing.sm}px;
      top: 0;
      ${
        isChatPanelOpen
          ? `
        padding-left: ${theme.spacing.sm}px;
        padding-right: ${theme.spacing.sm}px;
        padding-top: ${theme.spacing.lg}px;
        padding-bottom: ${theme.spacing.md}px;
        gap: ${theme.spacing.md}px;
      `
          : ''
      }

      @media (max-width: ${theme.breakpoints.lg + chatOffset - 1}px) {
        padding-left: ${theme.spacing.sm}px;
        padding-right: ${theme.spacing.sm}px;
        padding-top: ${theme.spacing.lg}px;
        padding-bottom: ${theme.spacing.md}px;
        gap: ${theme.spacing.md}px;
      }
      @media (max-width: ${theme.breakpoints.sm + chatOffset - 1}px) {
        padding-top: ${theme.spacing.xl}px;
      }
      @media (max-width: ${theme.breakpoints.xs + chatOffset - 1}px) {
        gap: 0px;
      }
    `
    }
  `;
});

const PageHeaderContainer = styled('div', {
  shouldForwardProp: (prop) => !customProps.includes(prop),
})<{
  isSidePanel?: boolean;
  chatOffset?: number;
}>(({ theme, isSidePanel, chatOffset: chatOffsetProp = 0 }) => {
  const chatOffset = chatOffsetProp;

  return `
    width: ${theme.other.screenWidth.lg};
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    flex-direction: column;
    justify-content: flex-start;
    gap: ${theme.other.spacing._16}px;
    transition: width 0.3s ease-in-out, gap 0.3s ease-in-out, padding 0.3s ease-in-out;

    ${
      isSidePanel
        ? `
      width: 100%;
      align-items: flex-start;
      flex-direction: column;
      position: relative;
      gap: ${theme.spacing.xl}px;
    `
        : `
      @media (max-width: ${theme.breakpoints.lg + chatOffset - 1}px) {
        width: 100%;
      }
      @media (max-width: ${theme.breakpoints.sm + chatOffset - 1}px) {
        align-items: flex-start;
        flex-direction: column;
        position: relative;
        gap: ${theme.other.spacing._16}px;
      }

      @media (max-width: ${theme.breakpoints.xs + chatOffset - 1}px) {
        padding-top: 16px;
      }
    `
    }
  `;
});

const PageHeaderTitle = styled(Title, {
  shouldForwardProp: (prop) => !customProps.includes(prop),
})<StickyHeader>(({ theme, isSidePanel, chatOffset: chatOffsetProp = 0 }) => {
  const chatOffset = chatOffsetProp;

  return `
    margin-top: ${isSidePanel ? 0 : theme.spacing.lg}px;
    transition: width 0.3s ease-in-out, font-size 0.3s ease-in-out, margin 0.3s ease-in-out;
    min-width: 0;
    overflow: hidden;

    ${
      isSidePanel
        ? `
      width: 100%;
      font-size: ${theme.fontSizes.xl}px;
      max-width: 100%;
    `
        : `
      font-size: ${theme.fontSizes.md * 2}px;
      width: ${theme.other.screenWidth.lg};
      max-width: 100%;

      @media (max-width: ${theme.breakpoints.lg + chatOffset - 1}px) {
        width: 100%;
        max-width: 100%;
      }
      @media (max-width: ${theme.breakpoints.xs + chatOffset - 1}px) {
        font-size: ${theme.fontSizes.xl}px;
        transform: translateX(0);
        max-width: 100%;
      }
    `
    }
  `;
});

const PageHeaderContent = styled('div', {
  shouldForwardProp: (prop) => !customProps.includes(prop),
})<{
  isSidePanel?: boolean;
  chatOffset?: number;
}>(({ theme, isSidePanel, chatOffset: chatOffsetProp = 0 }) => {
  const chatOffset = chatOffsetProp;

  return `
    display: flex;
    flex-wrap: wrap;
    transition: gap 0.3s ease-in-out, row-gap 0.3s ease-in-out, column-gap 0.3s ease-in-out;

    ${
      isSidePanel
        ? `
      row-gap: ${theme.fontSizes.lg / 2}px;
      column-gap: ${theme.fontSizes.xl}px;
    `
        : `
      gap: ${theme.fontSizes.md}px;

      @media (max-width: ${theme.breakpoints.sm + chatOffset - 1}px) {
        row-gap: ${theme.fontSizes.lg / 2}px;
        column-gap: ${theme.fontSizes.xl}px;
      }
    `
    }
  `;
});

const PageHeaderActions = styled('div', {
  shouldForwardProp: (prop) => !customProps.includes(prop),
})<StickyHeader>(({ theme, isSidePanel, chatOffset: chatOffsetProp = 0 }) => {
  const chatOffset = chatOffsetProp;

  return `
    display: flex;
    gap: ${theme.spacing.sm}px;
    align-items: center;

    ${
      isSidePanel
        ? `
      justify-content: stretch;
      width: '100%';
      display: flex;
    `
        : `
      @media (max-width: ${theme.breakpoints.xs + chatOffset - 1}px) {
        position: relative;
        right: 0;
      }
    `
    }
  `;
});

export { PageHeader, PageHeaderContainer, PageHeaderTitle, PageHeaderContent, PageHeaderActions };
